import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchApi } from 'gossamer-front-end';

type AttendanceProgressProps = {
  percentWatched: number; // 0 - 100
  isEligible?: boolean; // override if you want strict >= 80 logic elsewhere
  eventId: number;
  eventSlug?: string;
};

export function AttendanceProgress({ percentWatched, isEligible, eventId, eventSlug }: AttendanceProgressProps) {
  const clamped = Math.max(0, Math.min(100, percentWatched));
  const eligible = isEligible ?? clamped >= 80;
  const [attendanceRecorded, setAttendanceRecorded] = useState(false);

  useEffect(() => {
    if (eligible && !!eventId && !attendanceRecorded) {
      fetchApi('POST', `/main/user-record-attendance-playback`, { eventId })
        .then(() => {
          setAttendanceRecorded(true);
        })
        .catch((error) => {
          console.error('Error fetching attendance token:', error);
        });
    }
  }, [eligible, eventId]);

  return (
    <div className="z-0 w-full rounded-t-none rounded-2xl bg-white shadow-sm ring-1 mt-[-6px] pt-[6px] ring-black/5">
      <div className="flex items-stretch sm:items-center justify-center gap-4 p-5 flex-col sm:flex-row">
        {/* Left */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-900">Attendance progress</h3>

            {eligible && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-600 text-white">
                  {/* check icon */}
                  <svg viewBox="0 0 20 20" fill="currentColor" className="h-3 w-3" aria-hidden="true">
                    <path
                      fillRule="evenodd"
                      d="M16.704 5.29a1 1 0 0 1 .006 1.415l-7.25 7.3a1 1 0 0 1-1.42.003l-3.25-3.25a1 1 0 1 1 1.414-1.414l2.54 2.54 6.54-6.586a1 1 0 0 1 1.42-.008Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
                <span>80% reached</span>
              </span>
            )}
          </div>

          {/* Progress bar */}
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className={`h-full rounded-full ${eligible ? 'bg-blue-600' : 'bg-blue-600'}`}
              style={{ width: `${clamped}%` }}
              aria-label="Watched progress"
              aria-valuenow={clamped}
              aria-valuemin={0}
              aria-valuemax={100}
              role="progressbar"
            />
          </div>

          {/* Helper text */}
          <p className="mt-3 text-sm text-gray-600">
            {attendanceRecorded ? 'Your attendance is confirmed.' : 'Watch at least 80% to confirm attendance.'}
            {attendanceRecorded && !!eventSlug && (
              <Link
                to={`/website/event-feedback/${eventSlug}`}
                className="font-medium text-blue-600 hover:text-blue-500 ml-2"
              >
                Leave feedback
              </Link>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
